import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  agentToolsApi,
  type MCPConnectionDTO,
  type PaginatedMCPConnectionsResponse,
} from "../../../services/agent-tools-service";
import { mapConnectionDtoToUi } from "../adapters";
import type { MCPConnection } from "../types";
import { MCP_CONNECTIONS_QUERY_KEY } from "./useMCPConnections";

export const MCP_CONNECTION_DETAIL_QUERY_KEY = [
  ...MCP_CONNECTIONS_QUERY_KEY,
  "detail",
] as const;

type UseMCPConnectionResult = {
  connection: MCPConnection | null;
  dto: MCPConnectionDTO | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
};

// Search every cached list page for the matching row so the drawer can render
// instantly while the detail call is in flight.
const useListSeed = (id: string | null): MCPConnectionDTO | undefined => {
  const queryClient = useQueryClient();
  return useMemo(() => {
    if (!id) return undefined;
    const caches = queryClient.getQueriesData<PaginatedMCPConnectionsResponse>({
      queryKey: MCP_CONNECTIONS_QUERY_KEY,
    });
    for (const [key, data] of caches) {
      // The prefix match also returns sub-queries like ["mcp-connections",
      // "detail"|"test"|"tools", id] whose data is NOT a paginated list.
      // Only scan actual list pages: ["mcp-connections", page, pageSize].
      if (typeof key[1] !== "number") continue;
      const items = data?.items;
      if (!Array.isArray(items)) continue;
      const match = items.find((item) => item.id === id);
      if (match) return match;
    }
    return undefined;
  }, [id, queryClient]);
};

export const useMCPConnection = (
  id: string | null,
): UseMCPConnectionResult => {
  const seed = useListSeed(id);

  const query = useQuery({
    queryKey: [...MCP_CONNECTION_DETAIL_QUERY_KEY, id],
    queryFn: () => agentToolsApi.getMCPConnection(id as string),
    enabled: Boolean(id),
    initialData: seed,
  });

  const connection = useMemo(
    () => (query.data ? mapConnectionDtoToUi(query.data) : null),
    [query.data],
  );

  return {
    connection,
    dto: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
  };
};
